import { useCallback, useEffect, useState } from 'react';
import posthog from 'posthog-js';
import { PostHogErrorBoundary } from 'posthog-js/react';

import { OnlinePartnerId } from '@/lib/types/onlineTiPartners';
import { LOCALE } from '@/lib/constants/i18n';

import Banner from './Banner';
import WidgetDialog from './WidgetDialog';
import { useStore } from '../hooks/useStore';
import { useDialog } from '../hooks/useDialog';
import { useNavigator } from '../hooks/useNavigator';
import useCurrentLocale from '../hooks/useCurrentLocale';
import useCurrentCurrency from '../hooks/useCurrentCurrency';

import DeviceStep from '../pages/DeviceStep';
import ContactStep from '../pages/ContactStep';
import GradingStep from '../pages/GradingStep';
import CompleteStep from '../pages/CompleteStep';

if (process.env.NEXT_PUBLIC_POSTHOG_KEY) {
  posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY, {
    api_host: 'https://eu.posthog.com',
    autocapture: false,
    capture_pageview: false,
    capture_pageleave: false,
  });
}

type WidgetType = {
  partnerId: OnlinePartnerId;
  clientId: string | null;
  orderId: string | null;
  tradeInId: string | null;
  sku: string | null;
  ean: string | null;
  purchaseUrl: string | null;
  campaignCode: string | null;
  locale: LOCALE | null;
  small: boolean;
  CustomBanner: typeof Banner | null;
};

const routes = [
  <DeviceStep />,
  <GradingStep />,
  <ContactStep />,
  <CompleteStep />,
];

const Widget = ({
  partnerId,
  clientId,
  orderId,
  tradeInId,
  sku,
  ean,
  purchaseUrl,
  campaignCode,
  small,
  locale,
  CustomBanner,
}: WidgetType) => {
  const [ready, setReady] = useState(false);

  const setShowDialog = useDialog((state) => state.setShowDialog);
  const showDialog = useDialog((state) => state.showDialog);
  const getPartner = useStore((state) => state.getPartner);
  const setLocale = useStore((state) => state.setLocale);
  const partner = useStore((state) => state.partner);
  const campaign = useStore((state) => state.campaign);
  const tradeIn = useStore((state) => state.tradeIn);
  const activeRoute = useNavigator((state) => state.activeRoute);
  const currentSku = useStore((state) => state.sku);
  const currentLocale = useCurrentLocale();
  const currentCurrency = useCurrentCurrency();

  const getTradeIn = useStore((state) => state.getTradeIn);
  const updateTradeIn = useStore((state) => state.updateTradeIn);
  const setClientId = useStore((state) => state.setClientId);
  const setOrderId = useStore((state) => state.setOrderId);
  const setSku = useStore((state) => state.setSku);
  const setEan = useStore((state) => state.setEan);
  const setPurchaseUrl = useStore((state) => state.setPurchaseUrl);
  const setCampaignCode = useStore((state) => state.setCampaignCode);
  const setSmall = useStore((state) => state.setSmall);

  useEffect(() => {
    setSmall(small);
    setSku(sku);
    setEan(ean);
    setPurchaseUrl(purchaseUrl);
    setCampaignCode(campaignCode);
    setLocale(locale);
    setClientId(clientId || posthog.get_distinct_id());
    setOrderId(orderId);

    getPartner(partnerId)
      .then(() => getTradeIn(tradeInId))
      .then(() => setReady(true));
  }, [
    sku,
    ean,
    purchaseUrl,
    campaignCode,
    small,
    locale,
    partnerId,
    clientId,
    orderId,
  ]);

  useEffect(() => {
    if (!partner) return;

    const userProperties = {
      clientVersion: partner.clientVersion,
      sku: currentSku ?? undefined,
      campaign: campaignCode ?? undefined,
      campaignAmount: campaign?.amount,
      locale: currentLocale,
      currency: currentCurrency,
      clientId: clientId || posthog.get_distinct_id(),
      orderId,
      partnerId,
    };
    if (clientId) {
      posthog.identify(clientId, userProperties);

      if (orderId) {
        posthog.alias(orderId, clientId);
      }
    } else {
      posthog.setPersonProperties(userProperties);
    }
  }, [
    currentSku,
    campaignCode,
    currentLocale,
    currentCurrency,
    partnerId,
    clientId,
    orderId,
    partner,
    campaign,
  ]);

  useEffect(() => {
    // Keep clientId and orderId in sync
    if (
      tradeIn &&
      ((clientId && tradeIn.clientId !== clientId) ||
        (orderId && tradeIn.orderId !== orderId))
    ) {
      updateTradeIn({
        clientId: clientId ?? undefined,
        orderId: orderId ?? undefined,
      });
    }
  }, [tradeIn, clientId, orderId]);

  const closeModal = useCallback(() => {
    setShowDialog(false);
  }, [setShowDialog]);

  if (!partner || !ready) return null;

  const BannerComponent = CustomBanner ?? Banner;

  return (
    <PostHogErrorBoundary>
      <BannerComponent />
      {showDialog && (
        <WidgetDialog closeModal={closeModal}>
          {routes[activeRoute]}
        </WidgetDialog>
      )}
    </PostHogErrorBoundary>
  );
};

export default Widget;
